import { Link } from "@ignite-analytics/icons";
import { Button, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { useIntl } from "react-intl";

import Layout from "../Layout";
import { PoweredByFooter } from "../PoweredByFooter";
import { ShapeIcon } from "../ShapeIcon";

type Props = {
    reason:
        | "404"
        | "completedAssessment"
        | "completedCampaign"
        | "missingQuestionnaire"
        | "previewExpired"
        | "viewExpired"
        | "other";
    url?: string;
};

const MessagePage: React.FC<Props> = ({ reason = "other", url }) => {
    const { formatMessage } = useIntl();

    const cases: Record<Props["reason"], { header: string; description: string; closeHint?: string }> = {
        "404": {
            header: formatMessage({ defaultMessage: "This link is no longer active" }),
            description: formatMessage({
                defaultMessage:
                    "It looks like the request you're trying to access has been deleted or is no longer available.",
            }),
            closeHint: formatMessage({ defaultMessage: "You can safely close this window." }),
        },
        completedAssessment: {
            header: formatMessage({ defaultMessage: "Thank you for submitting your assessment!" }),
            description: formatMessage({ defaultMessage: "You can now close this window." }),
        },
        completedCampaign: {
            header: formatMessage({ defaultMessage: "Assessment sent!" }),
            description: formatMessage({ defaultMessage: "You can close this window or start a new assessment." }),
        },
        missingQuestionnaire: {
            header: formatMessage({ defaultMessage: "This questionnaire does not have any questions!" }),
            description: formatMessage({ defaultMessage: "You should let the person that sent it know." }),
        },
        previewExpired: {
            header: formatMessage({ defaultMessage: "This preview link has expired" }),
            description: formatMessage({
                defaultMessage:
                    "Preview links are valid for one hour. Open a new preview from the questionnaire in Ignite.",
            }),
            closeHint: formatMessage({ defaultMessage: "You can safely close this window." }),
        },
        viewExpired: {
            header: formatMessage({ defaultMessage: "This link has expired" }),
            description: formatMessage({
                defaultMessage:
                    "Links to view a submission are valid for one hour. Open the submission again in Ignite.",
            }),
            closeHint: formatMessage({ defaultMessage: "You can safely close this window." }),
        },
        other: {
            header: formatMessage({ defaultMessage: "Failed to load data." }),
            description: formatMessage({
                defaultMessage: "There has been an error while processing your request. Please try again later.",
            }),
        },
    };

    const { header, description, closeHint } = cases[reason];

    // Dead-end states read as deactivated, not actionable — mute the icon.
    const iconColor =
        reason === "404" || reason === "previewExpired" || reason === "viewExpired" || reason === "other"
            ? "gray"
            : "accent";

    return (
        <Layout>
            <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
                <Paper elevation={1} sx={{ width: "720px", paddingY: 6, paddingX: 3 }}>
                    <Stack alignItems="center" spacing={3}>
                        <ShapeIcon icon={<Link />} size="large" color={iconColor} />
                        <Typography variant="textXl" fontWeight="bold">
                            {header}
                        </Typography>
                        <Stack alignItems="center" spacing={1.5} width={500}>
                            <Typography variant="textSm" color="textSecondary">
                                {description}
                            </Typography>
                            {closeHint && (
                                <Typography variant="textSm" color="textSecondary">
                                    {closeHint}
                                </Typography>
                            )}
                        </Stack>
                        {url && (
                            <Button size="small" href={url} variant="contained" color="primary">
                                {formatMessage({ defaultMessage: "Back to assessment" })}
                            </Button>
                        )}
                    </Stack>
                </Paper>
                <Stack mt={6}>
                    <PoweredByFooter />
                </Stack>
            </Stack>
        </Layout>
    );
};

export default MessagePage;
